import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { SharedLanguageService } from '@language';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ShDroppanelSelectComponent } from '../../../../../../elements/components/droppanel-select/droppanel-select.component';
import { ShIconModule } from '../../../../../../elements/components/icon/icon.module';

import { ShClickOutsideDirective } from '../../../../../../elements/directives/click-outside/click-outside.directive';
import { ShIndeterminateDirective } from '../../../../../../form-controls/directives/indeterminate.directive';
import { FormSelectOption } from '../../../../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../../../../form-controls/entities/multi-form-select-options.entity';
import { ActionMode, SelectionState } from '../../entities/select-mode.entity';

@Component({
    standalone: true,
    selector: 'sh-action-mode-checkbox',
    templateUrl: './action-mode-checkbox.component.html',
    styleUrls: ['./action-mode-checkbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShDroppanelSelectComponent,
        ShIconModule,
        ShIndeterminateDirective,
        ShClickOutsideDirective
    ]
})
export class ShActionModeCheckboxComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    protected destroy$: Subject<void> = new Subject<void>();

    public totalPageItemsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public selectedPageItemsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public selectedItemsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    public selectedItemsLabel$: Observable<string>;

    private initialMode: ActionMode;
    private emitUpdates: boolean = false;

    // Dropdown state
    public initialSelectedDropdownValues: FormValue[];
    public dropdownOptions: MultiFormSelectOptions<ActionMode> = {
        options: [
            { value: ActionMode.BULK, label: this.translateService.instant('general.select-mode.bulk') },
            { value: ActionMode.BATCH, label: this.translateService.instant('general.select-mode.batch') }
        ],
        isLiveData: false
    };

    public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly actionMode$: Subject<ActionMode> = new Subject<ActionMode>();
    private actionModeOrInit$: Observable<ActionMode>;

    // Checkbox state
    public checkboxControl: UntypedFormControl = new UntypedFormControl();
    public isIndeterminate$: Observable<boolean>;

    // Base setup
    @Input() canBatchSelect: boolean;
    @Input() itemsSelectedLabelKey?: string;
    @Input() noItemsSelectedLabelKey?: string;

    @Input() set isInitialBulk(isInitialBulk: boolean) {
        this.initialMode = isInitialBulk ? ActionMode.BULK : ActionMode.BATCH;
    }

    // Computational inputs
    @Input() set totalPageItemsCount(total: number) {
        this.totalPageItemsCount$.next(total);
    }

    @Input() set selectedPageItemsCount(selected: number) {
        this.selectedPageItemsCount$.next(selected);
    }

    @Input() set selectedItemsCount(selected: number) {
        this.selectedItemsCount$.next(selected);
    }

    @Output() selectionChanged: EventEmitter<SelectionState> = new EventEmitter<SelectionState>();

    ngOnInit(): void {
        this.initInitialValues();

        // When external input changes => no need to emit
        this.initExternalInputStreams();
        this.initExternalInputListener();
    }

    ngAfterViewInit(): void {
        // When internal input changes => need to emit
        this.initControlChangeListener();

        this.emitUpdates = true;
    }

    ngOnDestroy(): void {
        this.destroy$.complete();
    }

    private initInitialValues(): void {
        this.initialSelectedDropdownValues = [{ value: this.initialMode, state: FormValueState.SET }];
    }

    // #region External input streams and listeners
    private initExternalInputStreams(): void {
        this.selectedItemsLabel$ = this.selectedItemsCount$.pipe(
            map(itemsCount => {
                if (itemsCount === 0) {
                    const labelKey: string = this.noItemsSelectedLabelKey || 'general.none-selected.other';
                    return this.translateService.instant(labelKey);
                } else {
                    const labelKey: string = this.itemsSelectedLabelKey || 'general.xx-selected.other';
                    return this.translateService.instant(labelKey, { amount: itemsCount });
                }
            })
        );

        this.actionModeOrInit$ = this.actionMode$.pipe(startWith(this.initialMode));

        this.isIndeterminate$ = combineLatest([
            this.totalPageItemsCount$,
            this.selectedItemsCount$,
            this.selectedPageItemsCount$,
            this.actionModeOrInit$
        ]).pipe(
            debounceTime(10),
            map(([totalPageItems, totalSelectedItems, selectedPageItems, actionMode]) =>
                this.isIndeterminateState(totalPageItems, totalSelectedItems, selectedPageItems, actionMode)
            )
        );
    }

    private initExternalInputListener(): void {
        combineLatest([this.totalPageItemsCount$, this.selectedPageItemsCount$])
            .pipe(debounceTime(10))
            .subscribe(([totalPageItems, selectedPageItems]) => {
                this.setCheckboxState(this.calculateCheckboxState(totalPageItems, selectedPageItems));
            });
    }
    // #endregion

    // #region Calculations
    private isIndeterminateState(
        totalPageItems: number,
        totalSelectedItems: number,
        selectedPageItems: number,
        actionMode: ActionMode
    ): boolean {
        const allSelectedOnPage: boolean = selectedPageItems === totalPageItems;

        if (allSelectedOnPage || actionMode === ActionMode.BATCH) {
            return false;
        }

        return totalSelectedItems > 0;
    }

    private calculateCheckboxState(totalPageItems: number, selectedPageItems: number): boolean {
        // If all selected on current page => return true
        return selectedPageItems === totalPageItems;
    }
    // #endregion

    // #region Control change listener
    private initControlChangeListener(): void {
        this.checkboxControl.valueChanges
            .pipe(withLatestFrom(this.actionModeOrInit$), takeUntil(this.destroy$))
            .subscribe(([checkboxState, actionMode]) => {
                // Deselect all on uncheck
                if (checkboxState === false) {
                    this.deselectAll(actionMode);
                    return;
                }
                // Select all on check
                this.selectAll(actionMode);
            });

        this.actionMode$.pipe(takeUntil(this.destroy$)).subscribe(actionMode => this.selectAll(actionMode));
    }
    // #endregion

    // #region Checkbox interactions
    private setCheckboxState(checkboxState: boolean): void {
        this.checkboxControl.setValue(checkboxState, { emitEvent: false });
    }
    // #endregion

    // #region Dropdown interactions
    public toggleDropdown(event: Event): void {
        // Ignore checkbox clicks
        if (event.target instanceof HTMLInputElement && event.target.tagName === 'INPUT') {
            return;
        }
        this.isOpen$.next(!this.isOpen$.value);
    }

    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    public onSelectionStateChange(formValueSelectOptions: FormValue<FormSelectOption<ActionMode>>[]): void {
        const selectedMode: ActionMode | undefined = formValueSelectOptions.find(
            option => option.state === FormValueState.SET
        )?.value.value;
        this.actionMode$.next(selectedMode ?? ActionMode.BULK);
    }
    // #endregion

    // #region Emitter
    private selectAll(actionMode: ActionMode): void {
        this.emitSelectionEvent(true, false, actionMode);
    }

    private deselectAll(actionMode: ActionMode): void {
        this.emitSelectionEvent(false, true, actionMode);
    }

    private emitSelectionEvent(selectAll: boolean, deselectAll: boolean, actionMode: ActionMode): void {
        if (this.emitUpdates) {
            this.selectionChanged.emit({
                selectAll: selectAll,
                deselectAll: deselectAll,
                actionMode
            });
        }
    }
    // #endregion
}
